import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { Theme, ThemeProvider as MuiThemeProvider } from '@mui/material/styles'
import { CssBaseline } from '@mui/material'
import { createThemeFromPreset, PaletteOption } from '../theme/theme'

type ThemeMode = 'light' | 'dark'

interface ThemeContextType {
  mode: ThemeMode
  palette: PaletteOption
  themeId: string
  theme: Theme
  toggleMode: () => void
  setPalette: (palette: PaletteOption) => void
  setThemeId: (id: string) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

export const useThemeContext = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useThemeContext must be used within ThemeProvider')
  }
  return context
}

interface ThemeProviderProps {
  children: ReactNode
}

const STORAGE_KEY = 'galatea_theme_mode'
const BASE_STORAGE_THEME_ID = 'galatea_theme_id'

function storageKeyByEmpresa(): string {
  if (typeof window === 'undefined') return BASE_STORAGE_THEME_ID
  const ctx = localStorage.getItem('user_context')
  try {
    if (ctx) {
      const parsed = JSON.parse(ctx)
      const emp: number | null | undefined = parsed?.empresa_id
      if (emp !== undefined && emp !== null) return `${BASE_STORAGE_THEME_ID}_${emp}`
    }
  } catch {}
  return BASE_STORAGE_THEME_ID
}

function getInitialMode(): ThemeMode {
  if (typeof window === 'undefined') return 'light'
  const saved = localStorage.getItem(STORAGE_KEY) as ThemeMode | null
  if (saved === 'light' || saved === 'dark') return saved
  try {
    const ctxStr = localStorage.getItem('user_context')
    if (ctxStr) {
      const ctx = JSON.parse(ctxStr)
      const m = ctx?.preferencias?.ui?.mode
      if (m === 'light' || m === 'dark') return m
    }
  } catch {}
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
  return prefersDark ? 'dark' : 'light'
}

function getInitialThemeId(): string {
  if (typeof window === 'undefined') return 'galatea-navy'
  const key = storageKeyByEmpresa()
  const stored = localStorage.getItem(key)
  if (stored) return stored
  try {
    const ctxStr = localStorage.getItem('user_context')
    if (ctxStr) {
      const ctx = JSON.parse(ctxStr)
      const tid = ctx?.preferencias?.ui?.themeId
      if (typeof tid === 'string' && tid.length > 0) return tid
    }
  } catch {}
  return 'galatea-navy'
}
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>(getInitialMode)

  // Paleta fixa em 'default' para evitar estados inconsistentes/bugs visuais
  const [palette] = useState<PaletteOption>('default')
  const [themeId, setThemeIdState] = useState<string>(getInitialThemeId)

  // Criar tema baseado no modo e paleta
  const theme = createThemeFromPreset(mode, themeId)

  // Sincronizar classe `dark` no DOM com o modo (Tailwind + layout)
  useEffect(() => {
    if (typeof document === 'undefined') return
    const root = document.documentElement
    if (mode === 'dark') {
      root.classList.add('dark')
      document.body.classList.add('dark')
    } else {
      root.classList.remove('dark')
      document.body.classList.remove('dark')
    }
  }, [mode])

  // Atualizar CSS variables usadas por Tailwind/utilitários com base no tema atual
  useEffect(() => {
    if (typeof document === 'undefined') return
    const root = document.documentElement
    const primary = theme?.palette?.primary?.main || '#3b82f6'
    const secondary = theme?.palette?.secondary?.main || '#64748b'
    root.style.setProperty('--color-primary-500', primary)
    root.style.setProperty('--color-secondary-500', secondary)
  }, [theme])

  const toggleMode = () => {
    setMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light'
      if (typeof window !== 'undefined') localStorage.setItem(STORAGE_KEY, next)
      return next
    })
  }

  const setPalette = (_newPalette: PaletteOption) => {
    // Paleta atualmente desabilitada; manter assinatura para compatibilidade.
    // No-op para evitar mudança de cores até definirmos novo design.
    console.warn('Palette switching is temporarily disabled. Using default palette.')
  }

  const setThemeId = (id: string) => {
    setThemeIdState(id)
    if (typeof window !== 'undefined') {
      const key = storageKeyByEmpresa()
      localStorage.setItem(key, id)
    }
  }

  return (
    <ThemeContext.Provider
      value={{
        mode,
        palette,
        themeId,
        theme,
        toggleMode,
        setPalette,
        setThemeId,
      }}
    >
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  )
}
